import { useEffect, useState } from "react";
import { Button } from "@repo/ui";
import { dictFor, languageName, useI18n, useT } from "./i18n";

// Plattform-Landing (Apex pulpo.cloud): verkauft die DIGITALE SPEISEKARTE.
// Gestalterische Leitidee: die Seite spricht die Sprache des Produkts — der
// Leistungs-Block ist als Speisekarte gesetzt (Papierfläche, gepunktete
// Führungslinien, „Preis" = inklusive), und das Telefon im Hero zeigt eine
// Mini-Karte mit eigenen Sprach-Pills, die NUR die Karte umschalten: das
// Kern-Feature führt sich selbst vor. Fünf UI-Sprachen (de/es/en/ru/ka) über
// @repo/i18n — dieselben wie im Back Office.

/** Kompakter Sprachumschalter (Endonyme; schreibt landing.lang). */
function LanguageSelect() {
  const { lang, setLang, available } = useI18n();
  return (
    <select
      aria-label="Language"
      value={lang}
      onChange={(e) => setLang(e.target.value)}
      className="h-9 rounded-md border border-border bg-background px-2 text-sm"
    >
      {available.map((code) => (
        <option key={code} value={code}>
          {languageName(code)}
        </option>
      ))}
    </select>
  );
}

/** Strichcode des Kassenbons als SVG mit festen Balken — ein CSS-Streifen-
 *  raster mit 1px-Strichen verzog sichtbar, sobald der Bon gedreht ist. */
const BARCODE = (() => {
  const widths = [
    2, 1, 3, 1, 1, 2, 1, 2, 3, 1, 2, 1, 1, 3, 2, 1, 2, 2, 1, 1, 3, 1, 2, 1,
    3, 1, 1, 2, 2, 1, 3, 2, 1, 1, 2, 3, 1, 2, 1, 2,
  ];
  let x = 0;
  const bars = widths.map((w, i) => {
    const bar = { x, w };
    x += w + (i % 3 === 0 ? 2 : 1);
    return bar;
  });
  return { bars, width: x - 1 };
})();

function Barcode({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden
      width={BARCODE.width}
      height={32}
      viewBox={`0 0 ${BARCODE.width} 32`}
      className={className}
      fill="currentColor"
    >
      {BARCODE.bars.map((bar) => (
        <rect key={bar.x} x={bar.x} y={0} width={bar.w} height={32} />
      ))}
    </svg>
  );
}

/** Telefon-Mockup: eine Mini-Speisekarte mit EIGENER Sprache — die Pills
 *  darunter schalten nur die Karte um (Live-Demo des Kern-Features), nicht
 *  die Seite. Der Seiten-Umschalter setzt die Karte auf seine Sprache mit. */
function PhoneDemo() {
  const { lang, available } = useI18n();
  const [phoneLang, setPhoneLang] = useState(lang);
  useEffect(() => setPhoneLang(lang), [lang]);
  const phone = dictFor(phoneLang).phone;
  return (
    <div className="mx-auto w-[272px] animate-rise-late rounded-[2.4rem] border border-paper-edge bg-ink/95 p-2.5 shadow-2xl motion-reduce:animate-none">
      <div className="rounded-[1.9rem] bg-paper px-5 pb-4 pt-5 text-ink">
        <div aria-hidden className="mx-auto mb-4 h-1.5 w-16 rounded-full bg-ink/15" />
        <p className="font-display text-xl font-semibold">{phone.restaurant}</p>
        <p className="text-xs uppercase tracking-[0.2em] text-copper">
          {phone.menuTitle}
        </p>
        <div className="my-3 border-t border-dotted border-ink/25" />
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-pine">
          {phone.category}
        </p>
        <ul className="space-y-2.5">
          {phone.dishes.map((dish) => (
            <li key={dish.name} className="flex items-baseline text-sm">
              <span className="min-w-0">{dish.name}</span>
              <span
                aria-hidden
                className="mx-1.5 min-w-4 flex-1 border-b border-dotted border-ink/30"
              />
              <span className="shrink-0 font-medium tabular-nums text-copper">
                {dish.price}
              </span>
            </li>
          ))}
        </ul>
        <div className="mt-5 flex flex-wrap justify-center gap-1.5">
          {available.map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => setPhoneLang(code)}
              aria-pressed={code === phoneLang}
              className={
                "rounded-full px-2.5 py-0.5 text-[11px] font-medium uppercase transition-colors " +
                (code === phoneLang
                  ? "bg-pine text-paper"
                  : "bg-ink/8 text-ink-soft hover:bg-ink/15")
              }
            >
              {code}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function App() {
  const t = useT();

  // Titel/Description folgen der aktiven Sprache (<html lang> setzt der
  // I18nProvider aus @repo/i18n zentral).
  useEffect(() => {
    document.title = t.meta.title;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", t.meta.description);
  }, [t]);

  return (
    <div className="min-h-dvh bg-background text-foreground">
      {/* Kopfzeile */}
      <header className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-6 py-5">
        <span className="font-display text-xl font-semibold tracking-tight">
          Pulpo
        </span>
        <div className="flex items-center gap-2">
          <LanguageSelect />
          <Button asChild variant="outline" size="sm">
            <a href="/admin/">{t.nav.login}</a>
          </Button>
        </div>
      </header>

      {/* Hero: Text links, Telefon-Demo rechts */}
      <section className="mx-auto grid max-w-5xl items-center gap-12 px-6 pb-20 pt-10 sm:pt-16 lg:grid-cols-[1fr_auto]">
        <div className="max-w-xl animate-rise motion-reduce:animate-none">
          <p className="mb-4 text-sm font-semibold uppercase tracking-wider text-copper">
            {t.hero.eyebrow}
          </p>
          <h1 className="text-balance font-display text-4xl font-semibold leading-[1.08] tracking-tight sm:text-6xl">
            {t.hero.title}
          </h1>
          <p className="mt-6 text-balance text-lg leading-relaxed text-muted-foreground">
            {t.hero.sub}
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Button asChild size="lg">
              <a href="/admin/">{t.hero.ctaStart}</a>
            </Button>
            <Button asChild variant="ghost" size="lg">
              <a href="https://a.pulpo.cloud">{t.hero.ctaDemo} →</a>
            </Button>
          </div>
        </div>
        <PhoneDemo />
      </section>

      {/* Drei Schritte (echte Sequenz) */}
      <section className="border-y border-border bg-muted/30">
        <div className="mx-auto max-w-5xl px-6 py-16">
          <h2 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">
            {t.steps.title}
          </h2>
          <ol className="mt-8 grid gap-10 sm:grid-cols-3">
            {t.steps.items.map((step, i) => (
              <li key={step.title}>
                <span
                  aria-hidden
                  className="font-display text-4xl font-semibold text-copper"
                >
                  {i + 1}.
                </span>
                <h3 className="mt-2 text-base font-semibold">{step.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                  {step.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Leistungen — gesetzt wie eine Speisekarte */}
      <section className="mx-auto max-w-5xl px-6 py-20">
        <div className="mx-auto max-w-2xl rounded-2xl border border-paper-edge bg-paper px-7 py-9 text-ink shadow-sm sm:px-10">
          <div className="text-center">
            <h2 className="font-display text-3xl font-semibold tracking-tight">
              {t.included.title}
            </h2>
            <p className="mt-1.5 text-sm text-ink-soft">{t.included.hint}</p>
            <div className="mx-auto mt-5 w-24 border-t border-dotted border-ink/30" />
          </div>
          <ul className="mt-7 space-y-5">
            {t.included.items.map((item) => (
              <li key={item.name}>
                <div className="flex items-baseline">
                  {/* Name darf umbrechen (ru/ka werden lang); die Punkte-Linie
                      behält eine Mindestbreite, der „Preis" bricht nie. */}
                  <span className="min-w-0 font-medium">{item.name}</span>
                  <span
                    aria-hidden
                    className="mx-2 min-w-6 flex-1 border-b border-dotted border-ink/30"
                  />
                  <span className="shrink-0 text-sm italic text-copper">
                    {t.included.price}
                  </span>
                </div>
                <p className="mt-0.5 text-sm text-ink-soft">{item.note}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Preis — als Kassenbon (Thermopapier, Monospace, Zackenkante), damit er
          sich klar von der Speisekarten-Karte darüber abhebt; eigenes Farbband
          trennt die beiden Papierflächen zusätzlich */}
      <section className="border-y border-border bg-muted/30">
        <div className="mx-auto max-w-5xl px-6 py-16">
          <div className="mx-auto w-fit [filter:drop-shadow(0_14px_18px_rgb(0_0_0/0.14))]">
            <div className="receipt w-[300px] -rotate-1 px-6 py-8 font-mono text-ink">
              <h2 className="text-center text-sm font-bold uppercase tracking-[0.2em]">
                {t.pricing.title}
              </h2>
              <p className="mt-1.5 text-center text-xs text-ink-soft">
                {t.pricing.hint}
              </p>
              <div className="my-4 border-t border-dashed border-ink/40" />
              <ul className="space-y-2 text-xs">
                {t.pricing.lines.map((line) => (
                  <li
                    key={line.name}
                    className="flex items-baseline justify-between gap-3"
                  >
                    <span className="min-w-0">{line.name}</span>
                    <span className="shrink-0 tabular-nums">{line.price}</span>
                  </li>
                ))}
              </ul>
              <div className="my-4 border-t border-dashed border-ink/40" />
              <div className="flex items-baseline justify-between gap-3 text-sm font-bold uppercase">
                <span>{t.pricing.totalLabel}</span>
                <span className="tabular-nums">{t.pricing.total}</span>
              </div>
              <div className="my-4 border-t border-dashed border-ink/40" />
              <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-ink-soft">
                {t.pricing.extrasTitle}
              </p>
              <ul className="space-y-2 text-xs text-ink-soft">
                {t.pricing.extras.map((extra) => (
                  <li
                    key={extra.name}
                    className="flex items-baseline justify-between gap-3"
                  >
                    <span className="min-w-0">{extra.name}</span>
                    <span className="shrink-0 tabular-nums">{extra.price}</span>
                  </li>
                ))}
              </ul>
              {/* Dank-Zeile + Strichcode: die Requisiten, die einen Bon als Bon
                  lesbar machen */}
              <p className="mt-5 text-center text-xs">{t.pricing.note}</p>
              <Barcode className="mx-auto mt-5 block" />
            </div>
          </div>
        </div>
      </section>

      {/* Abschluss */}
      <section className="mx-auto max-w-3xl px-6 pb-24 pt-20 text-center">
        <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
          {t.finale.title}
        </h2>
        <p className="mx-auto mt-3 max-w-md text-muted-foreground">
          {t.finale.sub}
        </p>
        <Button asChild size="lg" className="mt-7">
          <a href="/admin/">{t.finale.cta}</a>
        </Button>
      </section>

      {/* Fußzeile */}
      <footer className="border-t border-border">
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-2 px-6 py-8 text-sm text-muted-foreground sm:flex-row">
          <span>© {new Date().getFullYear()} Pulpo.cloud</span>
        </div>
      </footer>
    </div>
  );
}
